import Image from 'next/image'
import * as React from 'react'
import type { SocialProduct, SocialProductCard } from 'codegen/types'
import { motion } from 'framer-motion'
import { tw } from 'utils/classnames'
import { Button } from '@repo/design-system-cf/components/button'
import { Link } from '@repo/web-ui/components/link'
import { getProductUrl } from '../../../_utils'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'
import { prodCategoriesMap } from '@web/app/[locale]/(web)/masterfeed/utils/product-url'
import { decodeHtmlEntities } from 'utils/decode-html-entities'

// oxlint-disable-next-line complexity
export const DiscussionProductCard = ({
  product,
  size = 'regular',
}: {
  product?: SocialProductCard | SocialProduct
  size?: 'small' | 'regular'
}) => {
  const [isHovered, setIsHovered] = React.useState(false)

  if (!product) {
    return null
  }
  const productUrl = getProductUrl({
    authorId: product?.author?.id || '',
    productSlug: product?.slug || '',
  })

  const scaleVariants = {
    hover: { scale: 1.02 },
    initial: { scale: 1 },
  }

  let authorLink
  const profileLink = `${NEXT_PUBLIC_CF_URL}/crafter/${product?.author?.slug}/`
  if (product.category === 'Community') {
    authorLink = product?.author?.profileUrls?.crafter
      ? `${NEXT_PUBLIC_CF_URL}/creator/${product.author.slug}/`
      : profileLink
  } else if (product.category === 'Classes') {
    authorLink = product?.author?.profileUrls?.teacher
      ? product.author.profileUrls.teacher
      : profileLink
  } else {
    authorLink = product?.author?.profileUrls?.designer
      ? product.author.profileUrls.designer
      : profileLink
  }

  if (!authorLink.endsWith('/')) {
    authorLink = `${authorLink}/`
  }

  const matchingCategory = product?.author && prodCategoriesMap.get(product?.author.id)
  const categoryUrl = matchingCategory
    ? `${NEXT_PUBLIC_CF_URL}/${matchingCategory.slug}/`
    : `${NEXT_PUBLIC_CF_URL}/${product?.categorySlug}/`

  return (
    <div className="@container w-full flex-none rounded-[12px] sm:max-w-none overflow-hidden hover:shadow-[0px_2px_4px_2px_#1313151F,0px_1px_1px_0px_#1313151F] active:shadow-none focus-within:ring-2 focus-within:ring-cf-blue-700 focus-within:ring-offset-2 focus-within:ring-offset-white">
      <div
        className={tw(
          'bg-cf-grey-25 rounded-[12px] flex sm:flex-row flex-col w-full p-1 sm:gap-4 border border-[#1313151F]',
        )}
      >
        <motion.div
          variants={scaleVariants}
          initial="initial"
          animate={isHovered ? 'hover' : 'initial'}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          className={tw(
            'relative rounded-2 flex h-[190px] sm:h-[130px] sm:w-[180px] w-full items-center justify-center overflow-hidden hover:cursor-pointer shrink-0',
            size === 'small' ? 'sm:w-[140px] md:w-[200px] lg:w-[140px]' : 'sm:w-[200px]',
          )}
        >
          <Link
            href={productUrl}
            target="_blank"
            className={tw(
              'focus-within:outline-none focus-visible:outline-none focus-visible:shadow-none rounded-2 block w-full h-full relative overflow-hidden',
              size === 'small' ? 'tablet:h-[116px]' : 'tablet:h-[140px]',
            )}
            data-testid="discussion-product-card-link"
          >
            <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${product?.thumbnail})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(18px)',
                transform: 'scale(1.05)',
              }}
            />
            <Image
              src={product?.thumbnail || ''}
              alt={'Product Image'}
              fill
              className={tw(
                'relative h-full w-full object-contain block focus-within:outline-none focus-visible:outline-none focus-visible:shadow-none',
                size === 'small' ? 'sm:max-h-[120px]' : 'sm:§max-h-[132px]',
              )}
              unoptimized
            />
          </Link>
        </motion.div>
        <div className="flex-1 basis-0 min-w-0 sm:py-1 flex flex-col justify-between p-3 sm:p-0 rounded-2">
          <div className="flex flex-col sm:mr-2">
            <div className={tw('flex justify-between sm:flex-row flex-col-reverse gap-3')}>
              <motion.div
                variants={scaleVariants}
                initial="initial"
                className={tw(
                  'truncate font-semibold leading-6',
                  isHovered ? 'text-cf-purple-900' : 'text-cf-sf-gray-100',
                )}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
              >
                <Link
                  href={productUrl}
                  target="_blank"
                  className="text-14 sm:text-16 text-cf-grey-1000 truncate font-semibold focus-within:outline-none focus-visible:outline-none focus-visible:shadow-none"
                  data-testid="discussion-product-card-title-link"
                >
                  {decodeHtmlEntities(product?.title)}
                </Link>
              </motion.div>
              <Link
                href={categoryUrl}
                target="_blank"
                className="inline-flex w-fit rounded-4 text-nowrap bg-cf-theme-green-50 hover:bg-cf-theme-green-200 sm:py-1 sm:px-2 px-1 border border-[#1313151F] focus-visible:shadow-none focus-within:ring-1 focus-within:outline-cf-blue-700"
                data-testid="discussion-product-card-category-link"
              >
                <p className="text-cf-grey-600 font-semibold text-12">
                  {matchingCategory ? matchingCategory.name : product?.category}
                </p>
              </Link>
            </div>
            {product?.author?.slug ? (
              <div className="inline-flex flex-row items-center justify-start gap-2">
                <Image
                  src={product?.author?.avatarUrl || `https://avatar.iran.liara.run/public/${1}`}
                  alt={'Author Avatar'}
                  width={50}
                  height={50}
                  className="rounded-full aspect-square size-5 object-cover"
                />
                <p className="text-cf-grey-600 text-12 font-medium truncate">
                  <Link
                    href={authorLink}
                    className="hover:underline focus-within:outline-none focus-visible:shadow-none focus-visible:outline-none focus-visible:border focus-visible:border-cf-blue-300 rounded active:border active:border-cf-blue-300"
                    target="_blank"
                    data-testid="discussion-product-card-author-link"
                  >
                    By {decodeHtmlEntities(product?.author?.displayName || 'User')}
                  </Link>
                </p>
              </div>
            ) : null}
          </div>
          <Button
            asChild
            variant={'secondary'}
            size={size === 'small' ? 'small' : 'regular'}
            className="w-fit mt-6 sm:mt-3"
            data-testid="discussion-product-card-view-product-button"
          >
            <Link
              href={productUrl}
              target="_blank"
              className="focus-within:outline-none focus-visible:shadow-none"
            >
              {'View product'}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
